import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoinbaseEngUsersAccountManagementApiApiWorkspaceMember as WorkspaceMember } from '@cbhq/identity-api-hooks/account-management/types';

import { getNextChipFilterValues } from '../../../utils/makeChipFilterChange';

import { createApproverCounter } from './approvalApproverCounts';
import type { ApprovalProduct, ApprovalRule } from './approvalRulesDisplay';

export type ApprovalRuleState = Pick<ApprovalRule, 'approvers' | 'isEnabled' | 'requirementCount'>;

export type ChangedApprovalRule = {
  rule: ApprovalRule;
  state: ApprovalRuleState;
  eligibleApproverCount: number;
};

type RuleEditsById = Record<string, Partial<ApprovalRuleState>>;

function getRuleState(rule: ApprovalRule, ruleEdits: RuleEditsById): ApprovalRuleState {
  return {
    isEnabled: rule.isEnabled,
    approvers: rule.approvers,
    requirementCount: rule.requirementCount,
    ...ruleEdits[rule.id],
  };
}

function areRuleStatesEqual(firstRule: ApprovalRuleState, secondRule: ApprovalRuleState): boolean {
  const firstApprovers = new Set(firstRule.approvers);
  const secondApprovers = new Set(secondRule.approvers);

  return (
    firstRule.isEnabled === secondRule.isEnabled &&
    firstRule.requirementCount === secondRule.requirementCount &&
    firstApprovers.size === secondApprovers.size &&
    [...firstApprovers].every((approver) => secondApprovers.has(approver))
  );
}

export function useApprovalRuleEdits(
  approvalProducts: ApprovalProduct[],
  members: WorkspaceMember[] | undefined,
) {
  const [ruleEdits, setRuleEdits] = useState<RuleEditsById>({});

  const rulesById = useMemo<Record<string, ApprovalRule>>(
    () =>
      Object.fromEntries(
        approvalProducts.flatMap(({ rules }) => rules.map((rule) => [rule.id, rule] as const)),
      ),
    [approvalProducts],
  );
  const countEligibleApprovers = useMemo(() => createApproverCounter(members), [members]);

  useEffect(() => {
    setRuleEdits((currentEdits) => {
      const reconciledEdits = { ...currentEdits };
      let hasReconciledEdit = false;

      for (const ruleId of Object.keys(currentEdits)) {
        const rule = rulesById[ruleId];
        if (!rule || areRuleStatesEqual(rule, getRuleState(rule, currentEdits))) {
          delete reconciledEdits[ruleId];
          hasReconciledEdit = true;
        }
      }

      return hasReconciledEdit ? reconciledEdits : currentEdits;
    });
  }, [rulesById]);

  const [ruleStateById, maxRequiredApprovalsByRuleId] = useMemo(() => {
    const states: Record<string, ApprovalRuleState> = {};
    const maxRequiredApprovals: Record<string, number> = {};

    Object.values(rulesById).forEach((rule) => {
      const state = getRuleState(rule, ruleEdits);
      const eligibleApproverCount = countEligibleApprovers({
        approvers: state.approvers,
        resourceType: rule.resourceType,
        targetEntityId: rule.target.entityId,
      });

      states[rule.id] = {
        ...state,
        requirementCount:
          eligibleApproverCount == null
            ? state.requirementCount
            : Math.min(state.requirementCount, eligibleApproverCount),
      };
      maxRequiredApprovals[rule.id] = eligibleApproverCount ?? state.requirementCount;
    });

    return [states, maxRequiredApprovals] as const;
  }, [countEligibleApprovers, ruleEdits, rulesById]);

  const changedRules = useMemo<ChangedApprovalRule[]>(
    () =>
      Object.keys(ruleEdits).flatMap((ruleId) => {
        const rule = rulesById[ruleId];
        const state = ruleStateById[ruleId];
        if (!rule || !state || areRuleStatesEqual(rule, state)) {
          return [];
        }

        return [
          {
            rule,
            state,
            eligibleApproverCount: maxRequiredApprovalsByRuleId[ruleId] ?? 0,
          },
        ];
      }),
    [maxRequiredApprovalsByRuleId, ruleEdits, ruleStateById, rulesById],
  );

  const hasChanges = changedRules.length > 0;
  // Wait for members so requirementCount is clamped to the eligible member count before saving.
  const canSave = useMemo(
    () =>
      members != null &&
      hasChanges &&
      changedRules.every(({ state }) => !state.isEnabled || state.requirementCount >= 1),
    [changedRules, hasChanges, members],
  );

  const updateRuleEdit = useCallback(
    (
      ruleId: string,
      getPatch: (currentState: ApprovalRuleState) => Partial<ApprovalRuleState>,
      replaceCurrentEdit = false,
    ) => {
      setRuleEdits((currentEdits) => {
        const rule = rulesById[ruleId];
        if (!rule) {
          return currentEdits;
        }

        const currentState = getRuleState(rule, currentEdits);
        const patch = getPatch(currentState);
        const nextEdits = {
          ...currentEdits,
          [ruleId]: replaceCurrentEdit ? patch : { ...currentEdits[ruleId], ...patch },
        };

        if (replaceCurrentEdit && areRuleStatesEqual(rule, getRuleState(rule, nextEdits))) {
          delete nextEdits[ruleId];
        }

        return nextEdits;
      });
    },
    [rulesById],
  );

  const handleToggle = useCallback(
    (ruleId: string) =>
      updateRuleEdit(ruleId, (currentState) => ({ isEnabled: !currentState.isEnabled }), true),
    [updateRuleEdit],
  );

  const handleApproversChange = useCallback(
    (ruleId: string, value: string | string[] | null) =>
      updateRuleEdit(ruleId, (currentState) => ({
        approvers: getNextChipFilterValues(currentState.approvers, value),
      })),
    [updateRuleEdit],
  );

  const handleCountChange = useCallback(
    (ruleId: string, requirementCount: number) =>
      updateRuleEdit(ruleId, () => ({ requirementCount })),
    [updateRuleEdit],
  );

  const resetEdits = useCallback(() => {
    setRuleEdits({});
  }, []);

  return {
    ruleStateById,
    maxRequiredApprovalsByRuleId,
    changedRules,
    hasChanges,
    canSave,
    handleToggle,
    handleApproversChange,
    handleCountChange,
    resetEdits,
  };
}
